import { GameSource } from "./GameSource.js";
import type { MoveGameEvent } from "../../event/MoveGameEvent.js";
import type { RollGameEvent } from "../../event/RollGameEvent.js";
import { PlayerType } from "../../../../royalur/model/PlayerType.js";
import type { Agent } from "../../../../royalur/agent/Agent.js";
import type { Game } from "../../../../royalur/Game.js";
import { getTimeSeconds } from "../../../../util/utils.js";
import { GameMode } from "../../GameMode.js";
import { GameUpdateEvent } from "./GameUpdateEvent.js";
import { GamePlayers } from "../../../api/game/GamePlayers.js";
import { GamePlayerData } from "../../../api/game/GamePlayerData.js";
import type { BotType } from "../../BotType.js";
import type { AnalyticsProvider } from "../../../analytics/Analytics.js";
import type { LobbySettings } from "../../LobbySettings.js";
import type { MessageOutUploadGame } from "../../../api/game/MessageOutUploadGame.js";
import { BotLobbyPlayer } from "../../../lobby/BotLobbyPlayer.js";
import type { Engine } from "../../../../royalur/engine/Engine.js";
import type { GameAPI } from "../../../api/GameAPI.js";
import type { APIGamePreferences } from "../../../api/api_schema.js";
import type { GameClientControls } from "../../../GameClientControls.js";
import type { ReactionType } from "../../ReactionType.js";
import { LocalLobbyStatus } from "../status/LocalLobbyStatus.js";
import type { GameController } from "../GameController.js";
import { LobbyID } from "../../../lobby/LobbyID.js";


export class SelfPlayComputerGameSource extends GameSource {
    private readonly botType: BotType;
    private readonly botAgent: Agent;

    private currentAgentPromise: Promise<Game> | null = null;
    private gameController: GameController | null = null;

    constructor(
        initialClientControls: GameClientControls,
        initialPreferences: APIGamePreferences,
        analytics: AnalyticsProvider,
        gameAPI: GameAPI,
        setupSettings: LobbySettings,
        rules: Engine,
        botType: BotType,
        botAgent: Agent,
    ) {
        super(
            initialClientControls, initialPreferences, analytics, gameAPI, setupSettings,
            new LocalLobbyStatus(
                LobbyID.generateRandom(),
                GameMode.COMPUTER, botType, null, null, null,
            ),
            rules,
        );
        if (botType.isOnline())
            throw new Error("Bot type of " + botType.getName() + " requires an online game source");

        this.botType = botType;
        this.botAgent = botAgent;

        this.handleGameSetup();
    }

    override getControllerMaxDirectives(): number {
        // A value of 0 represents no limit.
        return 0;
    }

    override isLocalHumanPlayer(_playerType: PlayerType): boolean {
        return false;
    }

    private handleGameSetup(): void {
        const game = this.getRules().generateGame();

        const lightData = new GamePlayerData(1, PlayerType.LIGHT);
        const darkData = new GamePlayerData(2, PlayerType.DARK);

        const lightPlayer = new BotLobbyPlayer(this.botType, lightData);
        const darkPlayer = new BotLobbyPlayer(this.botType, darkData);

        this.updateGameAndPlayers(
            new GamePlayers(
                PlayerType.LIGHT,
                null,
                lightPlayer,
                darkPlayer,
            ),
            game, new GameUpdateEvent(true),
        );
        this.lobbyStatus.map(status => status.withCompleteGameID(null));
    }

    override setup(): () => void {
        const interval = setInterval(() => {
            this.monitor();
        }, 200);

        return () => {
            clearInterval(interval);
        };
    }

    linkGameController(gameController: GameController): () => void {
        this.gameController = gameController;
        return () => {
            this.gameController = null;
        };
    }

    monitor() {
        this.maybePlayAgentTurn();
    }

    override handleResign(): void {
        throw new Error("Unimplemented for self-play games");
    }

    override handleMove(_event: MoveGameEvent): void {
        throw new Error("Unimplemented for self-play games");
    }

    override handleRoll(_event: RollGameEvent): void {
        throw new Error("Unimplemented for self-play games");
    }

    override handleReaction(_player: PlayerType | null, _reaction: ReactionType) {
        throw new Error("Unimplemented for self-play games");
    }

    private maybePlayAgentTurn() {
        // Agent turn is in progress.
        if (this.currentAgentPromise !== null)
            return;
        if (this.gameController === null)
            return;
        if (this.gameController.getQueuedDirectiveCount() > 3)
            return;

        const game = this.game.get();
        if (game.isFinished()) {
            // Wait until this game finishes being animated.
            if (this.gameController.getQueuedDirectiveCount() > 0)
                return;

            this.handleGameSetup();
            return;
        }

        const startTime = getTimeSeconds();
        this.currentAgentPromise = this.botAgent.playTurn(game);

        this.currentAgentPromise.then((newGame: Game) => {
            this.currentAgentPromise = null;

            const duration = getTimeSeconds() - startTime;
            if (duration > 0.1) {
                console.log(`AI took ${duration.toFixed(3)} seconds`);
            }
            this.game.set(newGame, new GameUpdateEvent(false), game);
            this.maybePlayAgentTurn();
        }).catch((error: unknown) => {
            this.currentAgentPromise = null;
            console.error("Agent turn failed:", error);
        });
    }

    override shouldUploadGame(): boolean {
        return false;
    }

    override buildUploadGameMessage(): MessageOutUploadGame {
        throw new Error("Unimplemented for self-play games");
    }
}
